import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Debouncing and retry logic
  const lastSessionCheck = useRef<number>(0);
  const retryCount = useRef<number>(0);
  const maxRetries = 4;
  const debounceMs = 1000; // Max 1 session check per second

  // Centralized token cleanup
  const clearAuthTokens = useCallback(() => {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    if (!projectId) return;

    // Clear localStorage
    const keys = Object.keys(localStorage).filter(k => k.startsWith(`sb-${projectId}`));
    keys.forEach(k => localStorage.removeItem(k));
    
    // Clear cookies created by safe-storage polyfill
    document.cookie.split(';').forEach(c => {
      const name = c.split('=')[0].trim();
      if (name.startsWith(`sb-${projectId}`)) {
        document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
      }
    });
  }, []);

  // Exponential backoff calculator
  const getBackoffDelay = useCallback((attempt: number): number => {
    return Math.min(1000 * Math.pow(2, attempt), 8000); // 1s, 2s, 4s, 8s max
  }, []);

  // Debounced session refresh with exponential backoff
  const refreshSession = useCallback(async () => {
    const now = Date.now();
    
    // Debounce: prevent calling more than once per second
    if (now - lastSessionCheck.current < debounceMs) {
      console.log('⏱️ Auth: Debounced - skipping session check');
      return;
    }
    
    lastSessionCheck.current = now;

    try {
      const { data: { session: newSession }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('❌ Auth: Session check failed:', error);
        
        // Only clear session on actual auth errors, not network failures
        const isAuthError = error.message?.toLowerCase().includes('invalid') || 
                           error.message?.toLowerCase().includes('expired') ||
                           error.message?.toLowerCase().includes('revoked') ||
                           error.status === 401 ||
                           error.status === 403;
        
        // Handle 429 rate limit errors with exponential backoff (don't clear session)
        if (error.message?.includes('429') || error.message?.includes('Too Many Requests')) {
          retryCount.current++;
          
          if (retryCount.current <= maxRetries) {
            const delay = getBackoffDelay(retryCount.current - 1);
            console.log(`⏳ Auth: Rate limited, retrying in ${delay}ms (attempt ${retryCount.current}/${maxRetries})`);
            
            setTimeout(() => {
              refreshSession();
            }, delay);
            return;
          } else {
            console.warn('⚠️ Auth: Max retries reached for rate limiting, keeping existing session');
            retryCount.current = 0;
            return; // Keep existing session, don't force logout
          }
        }
        
        // For network errors, log but keep existing session
        if (!isAuthError) {
          console.warn('⚠️ Auth: Network error during session check, keeping existing session');
          return; // Keep existing session
        }
        
        // Only clear session for actual authentication errors
        console.error('❌ Auth: Authentication error detected, clearing session');
        clearAuthTokens();
        setSession(null);
        setUser(null);
        return;
      }
      
      // Success - reset retry count
      retryCount.current = 0;
      
      if (!newSession) {
        clearAuthTokens();
        setSession(null);
        setUser(null);
      } else {
        setSession(newSession);
        setUser(newSession.user);
      }
    } catch (err) {
      console.error('❌ Auth: Unexpected error during session check:', err);
      // Don't clear session on unexpected errors (likely network issues)
      // The existing session may still be valid
      console.warn('⚠️ Auth: Keeping existing session despite unexpected error');
    }
  }, [clearAuthTokens, getBackoffDelay]);

  // Initialize auth state (runs once on mount)
  useEffect(() => {
    let mounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;
        
        console.log('🔐 Auth: State changed:', event);
        
        // Synchronous state updates only - NO async operations here to prevent deadlock
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        // Reset retry count on successful auth events
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          retryCount.current = 0;
        }
        
        // Clear tokens on sign out
        if (event === 'SIGNED_OUT') {
          setTimeout(() => {
            clearAuthTokens();
          }, 0);
        }
      }
    );

    // THEN check for existing session (debounced)
    refreshSession().finally(() => {
      if (mounted) {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only

  const value = {
    user,
    session,
    loading,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
